import { useEffect, useRef } from 'react';
import { useMultiFormManager } from '@contexts/multi-form-manager-context';

/**
 * Hook para registrar automáticamente un formulario Formik en el MultiFormManager
 * 
 * @param {string} formName - Nombre único del formulario
 * @param {Object} customValidationSchema - Schema de validación personalizado (opcional)
 * @returns {Object} - Ref para conectar con Formik
 */
export const useFormRegistration = (formName, customValidationSchema = null) => {
  const formikRef = useRef();
  
  let registerForm, unregisterForm;
  try {
    const context = useMultiFormManager();
    registerForm = context.registerForm;
    unregisterForm = context.unregisterForm;
  } catch (error) {
    registerForm = () => {};
    unregisterForm = () => {};
  }

  useEffect(() => {
    if (formikRef.current && formName) {
      const formAPI = {
        /**
         * Valida el formulario y retorna resultado estructurado
         */
        validate: async () => {
          try {
            const errors = await formikRef.current.validateForm();
            
            // Marcar todos los campos como "touched" para mostrar errores
            const touchedFields = {};
            Object.keys(formikRef.current.values).forEach(field => {
              touchedFields[field] = true;
            });
            formikRef.current.setTouched(touchedFields);
            
            const isValid = Object.keys(errors).length === 0;
            
            return {
              isValid,
              errors,
              values: formikRef.current.values,
              touched: formikRef.current.touched
            };
          } catch (error) {
            console.error(`Error validating form ${formName}:`, error);
            return {
              isValid: false,
              errors: { general: error.message },
              values: formikRef.current?.values || {},
              touched: {}
            };
          }
        },

        /**
         * Obtiene los valores actuales del formulario
         */
        getValues: () => {
          return formikRef.current?.values || {};
        },

        /**
         * Obtiene los errores actuales del formulario
         */
        getErrors: () => {
          return formikRef.current?.errors || {};
        },

        /**
         * Verifica si el formulario es válido sin ejecutar validación
         */
        isValid: () => {
          return formikRef.current?.isValid || false;
        },

        /**
         * Ejecuta el submit del formulario
         */
        submit: async () => {
          if (formikRef.current) {
            await formikRef.current.submitForm();
            return formikRef.current.values;
          }
          throw new Error(`Form ${formName} no está inicializado`);
        },

        /**
         * Resetea el formulario a sus valores iniciales
         */
        reset: () => {
          if (formikRef.current) {
            formikRef.current.resetForm();
          }
        },

        /**
         * Establece valores en el formulario
         */
        setValues: (values) => {
          if (formikRef.current) {
            formikRef.current.setValues(values);
          }
        },

        /**
         * Establece errores en el formulario
         */
        setErrors: (errors) => {
          if (formikRef.current) {
            formikRef.current.setErrors(errors);
          }
        },

        // Metadatos útiles
        formName,
        customValidationSchema,
        isRegistered: true
      };

      // Registrar el formulario en el manager
      registerForm(formName, formAPI);
    }

    // Cleanup: desregistrar cuando el componente se desmonte
    return () => {
      if (formName) {
        unregisterForm(formName);
      }
    };
  }, [formName, registerForm, unregisterForm]); // ✅ Remover customValidationSchema de dependencias

  // Segundo useEffect para registrar cuando formikRef esté listo
  useEffect(() => {
    if (!formName) return;
    
    let timeoutId = null;
    let isRegistered = false;
    
    const checkAndRegister = () => {
      if (formikRef.current && !isRegistered) {
        isRegistered = true;
        
        const formAPI = {
          validate: async () => {
            try {
              const errors = await formikRef.current.validateForm();
              const touchedFields = {};
              Object.keys(formikRef.current.values).forEach(field => {
                touchedFields[field] = true;
              });
              formikRef.current.setTouched(touchedFields);
              const isValid = Object.keys(errors).length === 0;
              return {
                isValid,
                errors,
                values: formikRef.current.values,
                touched: formikRef.current.touched
              };
            } catch (error) {
              console.error(`Error validating form ${formName}:`, error);
              return {
                isValid: false,
                errors: { general: error.message },
                values: formikRef.current?.values || {},
                touched: {}
              };
            }
          },
          getValues: () => formikRef.current?.values || {},
          getErrors: () => formikRef.current?.errors || {},
          isValid: () => formikRef.current?.isValid || false,
          submit: async () => {
            if (formikRef.current) {
              await formikRef.current.submitForm();
              return formikRef.current.values;
            }
            throw new Error(`Form ${formName} no está inicializado`);
          },
          reset: () => {
            if (formikRef.current) {
              formikRef.current.resetForm();
            }
          },
          setValues: (values) => {
            if (formikRef.current) {
              formikRef.current.setValues(values);
            }
          },
          setErrors: (errors) => {
            if (formikRef.current) {
              formikRef.current.setErrors(errors);
            }
          },
          formName,
          customValidationSchema,
          isRegistered: true
        };

        registerForm(formName, formAPI);
      } else if (!isRegistered) {
        timeoutId = setTimeout(checkAndRegister, 100);
      }
    };

    // Iniciar el check
    checkAndRegister();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [formName, registerForm, customValidationSchema]);

  return formikRef;
};

/**
 * Hook alternativo más simple para casos básicos
 * 
 * @param {string} formName - Nombre único del formulario
 * @returns {Object} - Ref para conectar con Formik
 */
export const useSimpleFormRegistration = (formName) => {
  return useFormRegistration(formName, null);
}; 